import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, Image as ImageIcon } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { resolvePhoto } from '../utils/api';
import toast from 'react-hot-toast';
import './InvoiceModal.css';

// Format tanggal Indonesia
function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
}
function rupiah(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID'); }

// Nama file terformat: Invoice_INV-2026-12345678_Topik.png
function safeName(invoiceNumber, title, ext) {
  const inv = (invoiceNumber || 'INVOICE').replace(/[\/\\:*?"<>|]/g, '-');
  const t = (title || 'Artikel').replace(/[\/\\:*?"<>|]/g, '').trim().replace(/\s+/g, '-').slice(0, 40);
  return `Invoice_${inv}_${t}.${ext}`;
}

export default function InvoiceModal({ open, onClose, article }) {
  const { settings } = useSiteSettings();
  const paperRef = useRef(null);
  const [busy, setBusy] = useState('');

  if (!open || !article) return null;

  const p = article.payment || {};
  const sn = settings.siteName || 'Inspirasiya';
  const logo = settings.logoEmailUrl || settings.logoPortalUrl || settings.logoUrl || '';
  const topik = article.title || article.what || 'Artikel';
  const pelanggan = article.requestedBy?.name || '-';
  const metode = p.method === 'manual'
    ? `Transfer ${settings.bankName || ''}`
    : p.method === 'midtrans' ? 'Midtrans' : 'Gratis';

  // Tangkap kertas invoice sebagai canvas (html2canvas), import dinamis agar bundle ringan
  const capture = async () => {
    const html2canvas = (await import('html2canvas')).default;
    // Render dengan latar putih kertas invoice apa pun temanya (invoice dokumen resmi → latar terang)
    return html2canvas(paperRef.current, {
      scale: 2, backgroundColor: settings.colorInvoiceCard || '#FFFFFF', useCORS: true, logging: false,
    });
  };

  const downloadPNG = async () => {
    setBusy('png');
    try {
      const canvas = await capture();
      const link = document.createElement('a');
      link.download = safeName(p.invoiceNumber, topik, 'png');
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) { toast.error('Gagal membuat PNG'); }
    finally { setBusy(''); }
  };

  const downloadPDF = async () => {
    setBusy('pdf');
    try {
      const canvas = await capture();
      const { jsPDF } = await import('jspdf');
      const img = canvas.toDataURL('image/png');
      // A4 potrait, sesuaikan lebar gambar ke lebar halaman
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const margin = 12;
      const w = pw - margin * 2;
      const h = (canvas.height / canvas.width) * w;
      pdf.addImage(img, 'PNG', margin, margin, w, h);
      pdf.save(safeName(p.invoiceNumber, topik, 'pdf'));
    } catch (e) { toast.error('Gagal membuat PDF'); }
    finally { setBusy(''); }
  };

  return createPortal(
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal" onClick={e => e.stopPropagation()}>
        <div className="inv-modal-bar">
          <span className="inv-modal-title"><FileText size={15} /> Invoice</span>
          <div className="inv-modal-actions">
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={downloadPNG}>
              <ImageIcon size={13} /> {busy === 'png' ? '...' : 'PNG'}
            </button>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={downloadPDF}>
              <Download size={13} /> {busy === 'pdf' ? '...' : 'PDF'}
            </button>
            <button className="inv-close" onClick={onClose} aria-label="Tutup"><X size={18} /></button>
          </div>
        </div>

        <div className="inv-scroll">
          {/* Kertas invoice — yang ditangkap ke PNG/PDF */}
          <div className="inv-paper" ref={paperRef}>
            <div className="inv-head">
              <div>
                <div className="inv-head-label">INVOICE</div>
                <div className="inv-head-no">{p.invoiceNumber || '-'}</div>
              </div>
              {logo
                ? <img src={resolvePhoto(logo)} alt={sn} className="inv-logo" crossOrigin="anonymous" />
                : <div className="inv-brand">{sn}</div>}
            </div>

            <div className="inv-body">
              <span className="inv-lunas">LUNAS</span>
              <table className="inv-table">
                <tbody>
                  <tr><td>Tanggal</td><td>{fmtDate(p.paidAt)}</td></tr>
                  <tr><td>Pelanggan</td><td>{pelanggan}</td></tr>
                  <tr><td>Layanan</td><td>Pengajuan Artikel</td></tr>
                  <tr><td>Topik</td><td>{topik}</td></tr>
                  <tr><td>Metode</td><td>{metode}</td></tr>
                  {p.method === 'manual' && settings.bankAccountName && (
                    <tr><td>Rekening</td><td>{settings.bankAccountNo} — {settings.bankAccountName}</td></tr>
                  )}
                  <tr className="inv-total"><td>Total Dibayar</td><td>{rupiah(p.amount)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="inv-foot">
              {sn} · {settings.bankAccountName || 'PT. Nusa Inspira Teknologi'}<br />
              Invoice ini sah tanpa tanda tangan & dibuat otomatis oleh sistem.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
